/**
 * AccountRowMenu — "…" button at the start of each Accounts row
 * (Roomgrid row-menu pattern, mirroring the locks listings).
 *
 * Menu entries:
 *   Account info        → read-only modal fed from the list row
 *   Edit                → /apps/accounts/{id}
 *   Block / Unblock     → status change through the edit pipeline
 *   Cost center         → Credit consumption › Cost Centers
 *   Credit transactions → Credit consumption › Transactions
 */

import React, { useState } from "react";
import {
  useDelete,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import {
  MoreHoriz as MoreIcon,
  InfoOutlined as InfoIcon,
  EditOutlined as EditIcon,
  BlockOutlined as BlockIcon,
  LockOpenOutlined as UnblockIcon,
  AccountTreeOutlined as CostCenterIcon,
  ListAltOutlined as TransactionsIcon,
  DeleteOutlined as DeleteIcon,
  GroupOutlined as UsersIcon,
  VpnKeyOutlined as ApiKeysIcon,
} from "@mui/icons-material";
import { useCanManageAccounts } from "@/utils/permissions";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import type { AccountListRow, AccountStatus } from "../types/account";
import { setAccountStatus } from "../services/accountStatus";
import AccountInfoDialog from "./dialogs/AccountInfoDialog";
import AccountUsersDialog from "./dialogs/AccountUsersDialog";
import AccountApiKeysDialog from "./dialogs/AccountApiKeysDialog";

const i18n = "resources.accounts";

export const AccountRowMenu: React.FC = () => {
  const record = useRecordContext<AccountListRow>();
  const translate = useTranslate();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const canEdit = useCanManageAccounts();
  const canDelete = useCanManageAccounts();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const [deleteOne, { isLoading: deleting }] = useDelete();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [apiKeysOpen, setApiKeysOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!record) return null;

  const status = record.accountResponse?.accountStatus;
  const isBlocked = status === "blocked";
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  const t = (key: string, fallback: string) =>
    translate(`${i18n}.${key}`, { _: fallback, smart_count: 1 });

  const accountName = record?.accountResponse?.accountName ?? "";

  const confirmDelete = (): void => {
    deleteOne(
      "apps/accounts",
      { id: record.id, previousData: record },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify(`${i18n}.notifications.deleted`, {
            type: "success",
            messageArgs: { name: accountName, smart_count: 1 },
          });
          setDeleteOpen(false);
          refresh();
        },
        onError: (err: unknown) => {
          notify(
            extractErrorMessage(err, "ra.notification.http_error", translate),
            { type: "error" },
          );
        },
      },
    );
  };

  const changeStatus = async (next: AccountStatus) => {
    close();
    if (busy) return;
    setBusy(true);
    try {
      await setAccountStatus(record.id, next);
      notify(
        `${i18n}.notifications.${next === "blocked" ? "blocked" : "unblocked"}`,
        { type: "success" },
      );
      refresh();
    } catch (error: unknown) {
      notify(
        extractErrorMessage(
          error,
          `${i18n}.notifications.statusUpdateFailed`,
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={t("rowMenu.accountInfo", "Row actions")}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            close();
            setInfoOpen(true);
          }}
        >
          <ListItemIcon>
            <InfoIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText>{t("rowMenu.accountInfo", "Account info")}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            close();
            setUsersOpen(true);
          }}
        >
          <ListItemIcon>
            <UsersIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText>{t("rowMenu.viewUsers", "View users")}</ListItemText>
        </MenuItem>

        {canEdit && (
          <MenuItem
            onClick={() => {
              close();
              setApiKeysOpen(true);
            }}
          >
            <ListItemIcon>
              <ApiKeysIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText>{t("rowMenu.apiKeys", "API keys")}</ListItemText>
          </MenuItem>
        )}

        {canEdit && (
          <MenuItem
            onClick={() => {
              close();
              navigate(`/apps/accounts/${record.id}`);
            }}
          >
            <ListItemIcon>
              <EditIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText>{translate("ra.action.edit")}</ListItemText>
          </MenuItem>
        )}

        {canEdit && !isBlocked && (
          <MenuItem disabled={busy} onClick={() => changeStatus("blocked")}>
            <ListItemIcon>
              <BlockIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText>{t("rowMenu.block", "Block")}</ListItemText>
          </MenuItem>
        )}
        {canEdit && isBlocked && (
          <MenuItem disabled={busy} onClick={() => changeStatus("active")}>
            <ListItemIcon>
              <UnblockIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText>{t("rowMenu.unblock", "Unblock")}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        <MenuItem
          onClick={() => {
            close();
            navigate("/credit-consumption/cost-center");
          }}
        >
          <ListItemIcon>
            <CostCenterIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText>{t("rowMenu.costCenter", "Cost center")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            navigate({
              pathname: "/credit-consumption/credit-transaction",
              search: accountName
                ? `?accountName=${encodeURIComponent(accountName)}`
                : "",
            });
          }}
        >
          <ListItemIcon>
            <TransactionsIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText>
            {t("rowMenu.creditTransactions", "Credit transactions")}
          </ListItemText>
        </MenuItem>

        {canDelete && <Divider />}
        {canDelete && (
          <MenuItem
            onClick={() => {
              close();
              setDeleteOpen(true);
            }}
            sx={{
              color: rg.danger.line,
              "&:hover": { backgroundColor: rg.danger.bg },
            }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText>{translate("ra.action.delete")}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Roomgrid confirm: centered 420px, icon tile, danger CTA. */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { width: 420, maxWidth: "90vw" } }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "12px",
            pt: "26px !important",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              display: "grid",
              placeItems: "center",
              border: `1px solid ${rg.danger.line}`,
              borderRadius: "14px",
              backgroundColor: rg.danger.bg,
              color: rg.danger.fg,
            }}
          >
            <DeleteIcon sx={{ fontSize: 24 }} />
          </Box>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, letterSpacing: "-0.02em" }}>
            {translate(`${i18n}.delete.title`, {
              recordName: accountName,
              _: `Delete ${accountName}?`,
            })}
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: rg.text3, lineHeight: 1.55 }}>
            {translate(`${i18n}.delete.content`, {
              _: "Are you sure you want to delete this account? This action cannot be undone.",
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            variant="outlined"
            disabled={deleting}
            onClick={() => setDeleteOpen(false)}
          >
            {translate("ra.action.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleting}
            onClick={confirmDelete}
          >
            {translate("ra.action.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <AccountInfoDialog
        record={record}
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
      />

      <AccountUsersDialog
        accountId={String(record.id)}
        accountName={accountName}
        open={usersOpen}
        onClose={() => setUsersOpen(false)}
      />

      <AccountApiKeysDialog
        accountId={String(record.id)}
        accountName={accountName}
        applications={(record.applicationResponse ?? []).map((a) => ({
          id: a.id,
          name: a.name,
        }))}
        open={apiKeysOpen}
        onClose={() => setApiKeysOpen(false)}
      />
    </>
  );
};

export default AccountRowMenu;
